<script setup>
import {
    reactive,
    computed,
    nextTick,
    ref,
    watch,
    onMounted,
    onUnmounted,
} from 'vue';

const props = defineProps({
    uid: { type: String, default: '' },
});

const idSuffix = computed(() => (props.uid ? String(props.uid).replace(/[^a-zA-Z0-9_-]/g, '') : 'default'));

const stripCommas = (str) => (str || '').toString().replace(/,/g, '');
const withCommas = (value) => {
    const num = Number(value);
    if (!isFinite(num)) return '0';
    return Math.round(num).toLocaleString('en-NZ', { maximumFractionDigits: 0 });
};

const fields = reactive({
    property_value: '800,000',
    growth_rate: '5',
    years: '10',
});

const limits = {
    propertyMin: 100_000,
    propertyMax: 5_000_000,
    rateMin: 0,
    rateMax: 15,
    yearsMin: 1,
    yearsMax: 40,
};

function numProperty() {
    const raw = stripCommas(fields.property_value);
    const n = parseFloat(raw);
    return raw !== '' && isFinite(n) ? n : 0;
}

function numRate() {
    const n = parseFloat(fields.growth_rate);
    return isFinite(n) ? n : 0;
}

function numYears() {
    const n = parseInt(fields.years, 10);
    return isFinite(n) ? n : 0;
}

function clamp(n, min, max) {
    return Math.min(Math.max(n, min), max);
}

function handlePropertyInput(event) {
    const el = event.target;
    const raw = stripCommas(el.value);

    if (raw === '' || isNaN(raw) || /\.$/.test(el.value)) {
        fields.property_value = el.value;
        return;
    }

    const posFromEnd = el.value.length - el.selectionStart;
    fields.property_value = withCommas(raw);

    nextTick(() => {
        const newPos = el.value.length - posFromEnd;
        el.setSelectionRange(newPos, newPos);
    });
}

function handlePropertyBlur(event) {
    const raw = stripCommas(event.target.value);
    if (raw !== '' && !isNaN(raw)) {
        const clamped = clamp(parseFloat(raw), limits.propertyMin, limits.propertyMax);
        fields.property_value = withCommas(clamped);
    }
}

const principal = computed(() => clamp(numProperty(), limits.propertyMin, limits.propertyMax));
const rate = computed(() => clamp(numRate(), limits.rateMin, limits.rateMax));
const years = computed(() => clamp(numYears(), limits.yearsMin, limits.yearsMax));

const futureValue = computed(() =>
    principal.value * Math.pow(1 + rate.value / 100, years.value)
);

const capitalGain = computed(() => futureValue.value - principal.value);

const totalReturnPct = computed(() =>
    principal.value > 0 ? ((futureValue.value - principal.value) / principal.value) * 100 : 0
);

const projectionRows = computed(() => {
    const p = principal.value;
    const r = rate.value / 100;
    const y = years.value;
    const rows = [];
    for (let i = 0; i <= y; i++) {
        const value = p * Math.pow(1 + r, i);
        const gain = value - p;
        rows.push({
            key: i,
            label: i === 0 ? 'Today' : `Year ${i}`,
            value,
            gain,
        });
    }
    return rows;
});

function niceYMax(v) {
    if (!isFinite(v) || v <= 0) return 1_000_000;
    const pad = v * 0.08;
    const target = v + pad;
    const exp = Math.floor(Math.log10(target));
    const base = 10 ** exp;
    const n = target / base;
    let nice = 10;
    if (n <= 1) nice = 1;
    else if (n <= 2) nice = 2;
    else if (n <= 2.5) nice = 2.5;
    else if (n <= 5) nice = 5;
    return nice * base;
}

function formatAxis(n) {
    if (n === 0) return '$0';
    if (n >= 100_000) {
        return `$${(n / 1_000_000).toFixed(1)}M`;
    }
    if (n >= 1000) {
        return `$${(n / 1000).toFixed(1)}k`;
    }
    return `$${Math.round(n)}`;
}

function axisLeftPad(ymaxVal) {
    const samples = [0, ymaxVal * 0.5, ymaxVal].map((v) => formatAxis(v));
    const maxLen = Math.max(...samples.map((s) => s.length), 3);
    return Math.min(48, Math.max(28, 4 + maxLen * 4.25));
}

const CHART_W = 400;
const CHART_H = 220;

/**
 * @param {number} p principal
 * @param {number} rPercent annual rate %
 * @param {number} yearsFloat domain 0..yearsFloat (may be fractional while animating)
 */
function buildChartGeometry(p, rPercent, yearsFloat) {
    if (!p || yearsFloat < 0.001) return null;

    const r = rPercent / 100;
    const yMaxYears = Math.max(yearsFloat, 0.001);

    const w = CHART_W;
    const h = CHART_H;
    const padR = 12;
    const padT = 12;
    const padB = 40;

    const endVal = p * Math.pow(1 + r, yMaxYears);
    const ymax = niceYMax(Math.max(p, endVal));
    const ymin = 0;
    const padL = axisLeftPad(ymax);
    const cw = w - padL - padR;
    const ch = h - padT - padB;

    const xAt = (yr) => padL + (yr / yMaxYears) * cw;
    const yAt = (val) => padT + (1 - (val - ymin) / (ymax - ymin)) * ch;

    const seg = Math.max(32, Math.min(200, Math.ceil(yMaxYears * 10)));
    const points = [];
    for (let i = 0; i <= seg; i++) {
        const yr = (i / seg) * yMaxYears;
        const val = p * Math.pow(1 + r, yr);
        points.push({ x: xAt(yr), y: yAt(val), val, yr });
    }

    const yBase = yAt(p);
    const first = points[0];
    const last = points[points.length - 1];
    const plotBottom = h - padB;

    let lineD = `M ${first.x} ${first.y}`;
    for (let i = 1; i < points.length; i++) {
        lineD += ` L ${points[i].x} ${points[i].y}`;
    }

    const orangeAreaD = `${lineD} L ${last.x} ${yBase} L ${first.x} ${yBase} Z`;
    const grayBelowD =
        `M ${padL} ${plotBottom} L ${w - padR} ${plotBottom} L ${w - padR} ${yBase} L ${padL} ${yBase} Z`;

    const dashLine = `M ${padL} ${yBase} L ${w - padR} ${yBase}`;

    const tickCount = 5;
    const yTicks = [];
    for (let i = 0; i < tickCount; i++) {
        const t = (ymax * i) / (tickCount - 1);
        yTicks.push({
            y: yAt(t),
            text: formatAxis(t),
            value: t,
        });
    }

    const gridLines = yTicks.map((t) => ({
        y1: t.y,
        y2: t.y,
        x1: padL,
        x2: w - padR,
    }));

    const yInt = Math.max(1, Math.round(yearsFloat));
    const xLabels = [];
    const step = yInt <= 10 ? 2 : Math.max(1, Math.ceil(yInt / 5));
    for (let i = 0; i <= yInt; i += step) {
        xLabels.push({ x: xAt(Math.min(i, yMaxYears)), text: `${i}y` });
    }
    if (xLabels[xLabels.length - 1]?.text !== `${yInt}y`) {
        xLabels.push({ x: xAt(yMaxYears), text: `${yInt}y` });
    }

    return {
        w,
        h,
        padL,
        padR,
        padT,
        padB,
        cw,
        ch,
        yearDomain: yMaxYears,
        ymax,
        ymin,
        lineD,
        orangeAreaD,
        grayBelowD,
        dashLine,
        xLabels,
        yTicks,
        gridLines,
        points,
        yBase,
        plotBottom,
    };
}

/** Animated display values (smooth transitions when inputs change) */
const animP = ref(null);
const animR = ref(null);
const animY = ref(null);

const chartGeometry = computed(() => {
    const p = animP.value ?? principal.value;
    const r = animR.value ?? rate.value;
    const y = animY.value ?? years.value;
    return buildChartGeometry(p, r, y);
});

let chartAnimFrame = null;
let chartAnimStart = 0;
let chartAnimFrom = { p: 0, r: 0, y: 0 };
let chartAnimTo = { p: 0, r: 0, y: 0 };
const CHART_ANIM_MS = 480;

function easeOutCubic(t) {
    return 1 - (1 - t) ** 3;
}

function stopChartAnim() {
    if (chartAnimFrame != null) {
        cancelAnimationFrame(chartAnimFrame);
        chartAnimFrame = null;
    }
}

function tickChartAnim(now) {
    const t = Math.min(1, (now - chartAnimStart) / CHART_ANIM_MS);
    const e = easeOutCubic(t);
    animP.value = chartAnimFrom.p + (chartAnimTo.p - chartAnimFrom.p) * e;
    animR.value = chartAnimFrom.r + (chartAnimTo.r - chartAnimFrom.r) * e;
    animY.value = chartAnimFrom.y + (chartAnimTo.y - chartAnimFrom.y) * e;
    if (t < 1) {
        chartAnimFrame = requestAnimationFrame(tickChartAnim);
    } else {
        chartAnimFrame = null;
        animP.value = chartAnimTo.p;
        animR.value = chartAnimTo.r;
        animY.value = chartAnimTo.y;
    }
}

function startChartAnim() {
    const to = {
        p: principal.value,
        r: rate.value,
        y: years.value,
    };
    const from = {
        p: animP.value ?? to.p,
        r: animR.value ?? to.r,
        y: animY.value ?? to.y,
    };
    if (
        Math.abs(from.p - to.p) < 1 &&
        Math.abs(from.r - to.r) < 0.0001 &&
        Math.abs(from.y - to.y) < 0.0001
    ) {
        animP.value = to.p;
        animR.value = to.r;
        animY.value = to.y;
        return;
    }
    stopChartAnim();
    chartAnimFrom = from;
    chartAnimTo = to;
    chartAnimStart = performance.now();
    chartAnimFrame = requestAnimationFrame(tickChartAnim);
}

watch([principal, rate, years], startChartAnim);

onMounted(() => {
    animP.value = principal.value;
    animR.value = rate.value;
    animY.value = years.value;
});

onUnmounted(() => {
    stopChartAnim();
    stopHoverLoop();
});

const wrapRef = ref(null);
const svgRef = ref(null);
const tooltipRef = ref(null);

const chartHovered = ref(false);
/** Nearest integer year to pointer (magnet); drives tooltip values */
const hoverSnapYear = ref(0);
/** Target crosshair x in SVG user space (snapped to year magnet) */
const hoverTargetXSvg = ref(0);
/** Smoothed crosshair x in SVG user space */
const hoverSmoothXSvg = ref(0);

const HOVER_LERP = 0.22;
let hoverRaf = null;

function stopHoverLoop() {
    if (hoverRaf != null) {
        cancelAnimationFrame(hoverRaf);
        hoverRaf = null;
    }
}

function hoverLoop() {
    hoverRaf = null;
    if (!chartHovered.value || !chartGeometry.value) return;

    const tx = hoverTargetXSvg.value;
    const sx = hoverSmoothXSvg.value;
    const next = sx + (tx - sx) * HOVER_LERP;
    hoverSmoothXSvg.value = Math.abs(tx - sx) < 0.4 ? tx : next;

    if (chartHovered.value && Math.abs(hoverSmoothXSvg.value - tx) > 0.2) {
        hoverRaf = requestAnimationFrame(hoverLoop);
    }
}

function scheduleHoverLoop() {
    if (hoverRaf == null) hoverRaf = requestAnimationFrame(hoverLoop);
}

function clientToSvgX(clientX) {
    const svg = svgRef.value;
    const g = chartGeometry.value;
    if (!svg || !g) return g?.padL ?? 0;
    const rect = svg.getBoundingClientRect();
    const scaleX = g.w / rect.width;
    return (clientX - rect.left) * scaleX;
}

/** Integer year index 0..yCap from plot x (nearest magnet). */
function yearMagnetFromPlotX(svgX, g) {
    const { padL, cw, yearDomain } = g;
    if (cw <= 0) return 0;
    const yrFloat = ((svgX - padL) / cw) * yearDomain;
    const yCap = Math.max(0, Math.round(yearDomain));
    return clamp(Math.round(yrFloat), 0, yCap);
}

function magnetXForYear(year, g) {
    const x = g.padL + (year / g.yearDomain) * g.cw;
    return clamp(x, g.padL, g.w - g.padR);
}

function onChartEnter() {
    chartHovered.value = true;
}

function onChartMove(e) {
    const g = chartGeometry.value;
    if (!g) return;
    let x = clientToSvgX(e.clientX);
    const left = g.padL;
    const right = g.w - g.padR;
    x = clamp(x, left, right);
    const year = yearMagnetFromPlotX(x, g);
    hoverSnapYear.value = year;
    hoverTargetXSvg.value = magnetXForYear(year, g);
    if (!chartHovered.value) {
        chartHovered.value = true;
        hoverSmoothXSvg.value = hoverTargetXSvg.value;
    }
    scheduleHoverLoop();
}

function onChartLeave() {
    chartHovered.value = false;
    stopHoverLoop();
}

/** Tooltip: snapped year for labels; smooth x for line; dot on curve follows geometry at line x */
const hoverState = computed(() => {
    const g = chartGeometry.value;
    if (!g) return null;

    const p = principal.value;
    const rPct = rate.value;
    const r = rPct / 100;
    const x = hoverSmoothXSvg.value;
    const { padL, cw, yearDomain, ymax, ymin, padT, ch } = g;

    const snapYear = hoverSnapYear.value;
    const valueAtMagnet = p * Math.pow(1 + r, snapYear);

    const yrAlongLine = clamp(((x - padL) / cw) * yearDomain, 0, yearDomain);
    const valueOnCurve = p * Math.pow(1 + r, yrAlongLine);
    const yCurve = padT + (1 - (valueOnCurve - ymin) / (ymax - ymin)) * ch;
    const yPrincipal = padT + (1 - (p - ymin) / (ymax - ymin)) * ch;

    const yearTitle = `Year ${snapYear}`;

    return {
        xSvg: x,
        snapYear,
        yearTitle,
        originalValue: p,
        value: valueAtMagnet,
        yCurve,
        yPrincipal,
        plotLeft: padL,
        plotRight: g.w - g.padR,
    };
});

/** Tooltip position in px inside .cg-chart-wrap (clamped to plot band) */
const tooltipStyle = computed(() => {
    if (!chartHovered.value || !hoverState.value || !chartGeometry.value) {
        return { display: 'none' };
    }

    const g = chartGeometry.value;
    const h = hoverState.value;
    const wrap = wrapRef.value;
    const tip = tooltipRef.value;
    if (!wrap) return { display: 'none' };

    const wrapRect = wrap.getBoundingClientRect();
    const scale = wrapRect.width / g.w;

    const plotLeftPx = h.plotLeft * scale;
    const plotRightPx = h.plotRight * scale;

    const linePx = h.xSvg * scale;
    const tipW = Math.max(tip?.offsetWidth ?? 0, 172);
    const gap = 10;

    let left = linePx + gap;
    if (left + tipW > plotRightPx) {
        left = linePx - gap - tipW;
    }
    if (left < plotLeftPx) {
        left = plotLeftPx;
    }
    if (left + tipW > plotRightPx) {
        left = Math.max(plotLeftPx, plotRightPx - tipW);
    }

    const top = Math.max(6, (g.padT / g.h) * wrapRect.height);

    return {
        display: 'block',
        left: `${left}px`,
        top: `${top}px`,
    };
});

function onRateInput(e) {
    fields.growth_rate = e.target.value;
}

function onYearsInput(e) {
    fields.years = e.target.value.replace(/\D/g, '') || '1';
}

function handleRateBlur() {
    const n = clamp(numRate(), limits.rateMin, limits.rateMax);
    fields.growth_rate = Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function handleYearsBlur() {
    const n = clamp(numYears(), limits.yearsMin, limits.yearsMax);
    fields.years = String(n);
}
</script>

<template>
    <div class="capital-growth text-inter w-full">
        <div class="container">
            <div class="row row-cg-calculator items-start">
                <div class="column column-cg-inputs" data-fade-in>
                    <div class="block-input-section-cg">
                        <div class="input-container">
                            <label :for="`cg-property${idSuffix}`">
                                Current property value
                                <span class="c-highlight" aria-hidden="true" />
                            </label>
                            <div class="input_container row-1">
                                <div class="input_value_con theme_dark">
                                    <span class="symbol">$</span>
                                </div>
                                <input
                                    :id="`cg-property${idSuffix}`"
                                    type="text"
                                    inputmode="decimal"
                                    pattern="[0-9,]*[.]?[0-9]*"
                                    class="calc-input"
                                    required
                                    :value="fields.property_value"
                                    @input="handlePropertyInput"
                                    @blur="handlePropertyBlur"
                                >
                            </div>
                        </div>

                        <div class="input-container">
                            <label :for="`cg-rate${idSuffix}`">
                                Annual growth rate
                                <span class="c-highlight" aria-hidden="true" />
                            </label>
                            <div class="input_container input_container--suffix-end-lg row-1">
                                <div class="input_value_con theme_dark">
                                    <span class="symbol">%</span>
                                </div>
                                <input
                                    :id="`cg-rate${idSuffix}`"
                                    type="text"
                                    inputmode="decimal"
                                    class="calc-input"
                                    required
                                    :value="fields.growth_rate"
                                    @input="onRateInput"
                                    @blur="handleRateBlur"
                                >
                            </div>
                            <p class="note">
                                NZ historical average: ~7% p.a. (varies by region)
                            </p>
                        </div>

                        <div class="input-container">
                            <label :for="`cg-years${idSuffix}`">
                                Investment period
                                <span class="c-highlight" aria-hidden="true" />
                            </label>
                            <div class="input_container input_container--suffix-end-lg row-1">
                                <div class="input_value_con theme_dark">
                                    <p class="word">
                                        Years
                                    </p>
                                </div>
                                <input
                                    :id="`cg-years${idSuffix}`"
                                    type="text"
                                    inputmode="numeric"
                                    class="calc-input"
                                    required
                                    :value="fields.years"
                                    @input="onYearsInput"
                                    @blur="handleYearsBlur"
                                >
                            </div>
                        </div>
                    </div>

                    <div class="cg-results">
                        <div class="cg-result-row">
                            <p class="cg-result-label">
                                Future value (in {{ years }} {{ years === 1 ? 'year' : 'years' }})
                            </p>
                            <p class="cg-result-value">
                                ${{ withCommas(futureValue) }}
                            </p>
                        </div>
                        <div class="cg-result-row">
                            <p class="cg-result-label">
                                Capital gain
                            </p>
                            <p class="cg-result-value">
                                ${{ withCommas(capitalGain) }}
                            </p>
                        </div>
                        <div class="cg-result-row">
                            <p class="cg-result-label">
                                Total return
                            </p>
                            <p class="cg-result-value">
                                {{ Math.round(totalReturnPct) }}%
                            </p>
                        </div>
                    </div>
                </div>

                <div class="column column-cg-chart" data-fade-in-up>
                    <div class="cg-chart-card">
                        <h3 class="cg-chart-title">
                            Property value growth
                        </h3>
                        <p class="cg-chart-sub">
                            The orange area shows capital growth above your original ${{ withCommas(principal) }} investment.
                        </p>
                        <div v-if="chartGeometry" ref="wrapRef" class="cg-chart-wrap">
                            <svg
                                ref="svgRef"
                                class="cg-chart-svg h-auto w-full max-w-full"
                                :viewBox="`0 0 ${chartGeometry.w} ${chartGeometry.h}`"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g
                                    v-for="(gl, i) in chartGeometry.gridLines"
                                    :key="'g' + i"
                                >
                                    <line
                                        :x1="gl.x1"
                                        :y1="gl.y1"
                                        :x2="gl.x2"
                                        :y2="gl.y2"
                                        stroke="#A9A9A9"
                                        stroke-width="1"
                                    />
                                </g>

                                <path
                                    :d="chartGeometry.grayBelowD"
                                    fill="#E8E8E8"
                                    class="cg-chart-path cg-chart-path--fill"
                                />
                                <path
                                    :d="chartGeometry.orangeAreaD"
                                    fill="rgba(255, 85, 0, 0.28)"
                                    class="cg-chart-path cg-chart-path--fill"
                                />
                                <path
                                    :d="chartGeometry.dashLine"
                                    fill="none"
                                    stroke="#575756"
                                    stroke-width="1"
                                    stroke-dasharray="4 3"
                                    class="cg-chart-path cg-chart-path--stroke"
                                />
                                <path
                                    :d="chartGeometry.lineD"
                                    fill="none"
                                    stroke="#FF7144"
                                    stroke-width="2.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="cg-chart-path cg-chart-path--stroke"
                                />

                                <g v-if="chartHovered && hoverState" class="cg-chart-hover-layer">
                                    <line
                                        :x1="hoverState.xSvg"
                                        :y1="chartGeometry.padT"
                                        :x2="hoverState.xSvg"
                                        :y2="chartGeometry.plotBottom"
                                        stroke="#A9A9A9"
                                        stroke-width="1"
                                        class="cg-chart-crosshair"
                                    />
                                    <circle
                                        :cx="hoverState.xSvg"
                                        :cy="hoverState.yPrincipal"
                                        r="4.5"
                                        fill="#fff"
                                        stroke="#575756"
                                        stroke-width="1.5"
                                    />
                                    <circle
                                        :cx="hoverState.xSvg"
                                        :cy="hoverState.yCurve"
                                        r="4.5"
                                        fill="#fff"
                                        stroke="#FF7144"
                                        stroke-width="1.5"
                                    />
                                </g>

                                <g
                                    v-for="(t, i) in chartGeometry.yTicks"
                                    :key="'y' + i"
                                >
                                    <text
                                        :x="chartGeometry.padL - 4"
                                        :y="t.y + 4"
                                        text-anchor="end"
                                        fill="#A9A9A9"
                                        style="font-size: 8px;"
                                    >{{ t.text }}</text>
                                </g>
                                <g
                                    v-for="(xl, i) in chartGeometry.xLabels"
                                    :key="'x' + i"
                                >
                                    <text
                                        :x="xl.x"
                                        :y="chartGeometry.h - 12"
                                        text-anchor="middle"
                                        fill="#A9A9A9"
                                        style="font-size: 8px;"
                                    >{{ xl.text }}</text>
                                </g>

                                <rect
                                    class="cg-chart-hit"
                                    :x="chartGeometry.padL"
                                    :y="chartGeometry.padT"
                                    :width="chartGeometry.cw"
                                    :height="chartGeometry.ch"
                                    fill="transparent"
                                    @pointerenter="onChartEnter"
                                    @pointermove="onChartMove"
                                    @pointerleave="onChartLeave"
                                />
                            </svg>

                            <div
                                v-show="chartHovered && hoverState"
                                ref="tooltipRef"
                                class="cg-chart-tooltip"
                                :style="tooltipStyle"
                            >
                                <template v-if="hoverState">
                                    <p class="cg-chart-tooltip__title">
                                        {{ hoverState.yearTitle }}
                                    </p>
                                    <p class="cg-chart-tooltip__meta">
                                        originalValue : {{ Math.round(hoverState.originalValue) }}
                                    </p>
                                    <p class="cg-chart-tooltip__value">
                                        value : {{ Math.round(hoverState.value) }}
                                    </p>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="cg-post-chart w-full" data-fade-in>
                <div class="row row-cg-table">
                    <div class="cg-table-section">
                        <h3 class="cg-table-title">
                            Year-by-year projection
                        </h3>
                        <div class="cg-table-scroll">
                            <table class="cg-table">
                                <thead>
                                    <tr>
                                        <th>Year</th>
                                        <th>Property value</th>
                                        <th>Capital gain</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="row in projectionRows" :key="row.key">
                                        <td class="font-medium text-primary-three">
                                            {{ row.label }}
                                        </td>
                                        <td>${{ withCommas(row.value) }}</td>
                                        <td>
                                            <span v-if="row.key === 0" class="cg-gain-none">–</span>
                                            <span v-else class="cg-gain">+${{ withCommas(row.gain) }}</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <p class="cg-footnote-callout">
                            <strong>Note:</strong>
                            Property values can go down as well as up. This calculator assumes consistent growth which is rarely the case in real markets. Past performance is not indicative of future results.
                        </p>
                    </div>
                </div>

                <div class="row row-cg-footnotes">
                    <p class="cg-footnote-callout">
                        <strong>Disclaimer:</strong>
                        This calculator provides estimates only and should not be relied upon for financial decisions. Please seek professional advice for your situation.
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>
